import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../../components/ui/Card';
type TabType = 'cleaning' | 'maintenance';
type CleaningIntervention = {
  id: string;
  date: string;
  dateFormatted: string;
  time: string;
  supplier: string;
  operator: string;
  duration: string;
};
type MaintenanceIntervention = {
  id: string;
  date: string;
  dateFormatted: string;
  problemType: string;
  problemEmoji: string;
  description: string;
  severity: 'minor' | 'medium' | 'severe';
  resolvedBy: string;
  interventionTime: string;
};
// Mock data
const mockCleaningInterventions: {
  month: string;
  interventions: CleaningIntervention[];
}[] = [{
  month: 'Gennaio 2025',
  interventions: [{
    id: 'c1',
    date: '2025-01-28',
    dateFormatted: '28 Gen 2025',
    time: '09:00 - 11:30',
    supplier: 'CoopService',
    operator: 'Mario R.',
    duration: '2h 30min'
  }, {
    id: 'c2',
    date: '2025-01-21',
    dateFormatted: '21 Gen 2025',
    time: '09:15 - 11:00',
    supplier: 'CoopService',
    operator: 'Laura B.',
    duration: '1h 45min'
  }, {
    id: 'c3',
    date: '2025-01-14',
    dateFormatted: '14 Gen 2025',
    time: '08:30 - 10:45',
    supplier: 'CoopService',
    operator: 'Mario R.',
    duration: '2h 15min'
  }]
}, {
  month: 'Dicembre 2024',
  interventions: [{
    id: 'c4',
    date: '2024-12-28',
    dateFormatted: '28 Dic 2024',
    time: '09:00 - 11:00',
    supplier: 'CoopService',
    operator: 'Anna R.',
    duration: '2h'
  }]
}];
const mockMaintenanceInterventions: {
  month: string;
  interventions: MaintenanceIntervention[];
}[] = [{
  month: 'Gennaio 2025',
  interventions: [{
    id: 'm1',
    date: '2025-01-25',
    dateFormatted: '25 Gen 2025',
    problemType: 'Oggetto Rotto',
    problemEmoji: '🔧',
    description: 'Rubinetto bagno',
    severity: 'severe',
    resolvedBy: 'IdraulicoPro Srl',
    interventionTime: '45min'
  }, {
    id: 'm2',
    date: '2025-01-18',
    dateFormatted: '18 Gen 2025',
    problemType: 'Problema Elettrico',
    problemEmoji: '⚡',
    description: 'Presa cucina non funzionante',
    severity: 'medium',
    resolvedBy: 'ElettroFix',
    interventionTime: '30min'
  }]
}];
export function InterventionsHistoryPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('cleaning');
  const tabs: {
    id: TabType;
    label: string;
  }[] = [{
    id: 'cleaning',
    label: t.host.interventionsHistory.tabCleaning
  }, {
    id: 'maintenance',
    label: t.host.interventionsHistory.tabMaintenance
  }];
  const handleInterventionClick = (id: string, type: TabType) => {
    navigate(`/host/intervention/${id}?type=${type}`);
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1135">
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8" data-id="element-1136">
        {/* Header */}
        <motion.div className="flex items-center gap-4 mb-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1137">
          <button onClick={() => navigate('/host')} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-1138">
            <ArrowLeft className="w-6 h-6 text-gray-600" data-id="element-1139" />
          </button>
          <h1 className="text-2xl font-bold text-navy" data-id="element-1140">
            {t.host.interventionsHistory.title}
          </h1>
        </motion.div>

        {/* Tabs */}
        <motion.div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} data-id="element-1141">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-1142">
              {tab.label}
            </button>)}
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait" data-id="element-1143">
          {activeTab === 'cleaning' ? <motion.div key="cleaning" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="space-y-8" data-id="element-1144">
              {mockCleaningInterventions.map((group, groupIndex) => <div key={group.month} data-id="element-1145">
                  <h3 className="text-sm font-semibold text-gray-500 mb-3" data-id="element-1146">
                    {group.month}
                  </h3>

                  {/* Table Card */}
                  <Card className="overflow-hidden" data-id="element-1147">
                    <div className="overflow-x-auto" data-id="element-1148">
                      <table className="w-full" data-id="element-1149">
                        <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1150">
                          <tr data-id="element-1151">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1152">
                              Data
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1153">
                              Orario
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1154">
                              Fornitore
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1155">
                              Operatore
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1156">
                              Durata
                            </th>
                            <th className="px-4 py-3" data-id="element-1157"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200" data-id="element-1158">
                          {group.interventions.map((intervention, index) => <motion.tr key={intervention.id} initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: groupIndex * 0.1 + index * 0.05
                    }} onClick={() => handleInterventionClick(intervention.id, 'cleaning')} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-1159">
                              <td className="px-4 py-4 text-sm font-medium text-navy" data-id="element-1160">
                                {intervention.dateFormatted}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1161">
                                {intervention.time}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1162">
                                {intervention.supplier}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1163">
                                {intervention.operator}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1164">
                                {intervention.duration}
                              </td>
                              <td className="px-4 py-4 text-right" data-id="element-1165">
                                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-deep transition-colors inline-block" data-id="element-1166" />
                              </td>
                            </motion.tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>)}
            </motion.div> : <motion.div key="maintenance" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-8" data-id="element-1167">
              {mockMaintenanceInterventions.map((group, groupIndex) => <div key={group.month} data-id="element-1168">
                  <h3 className="text-sm font-semibold text-gray-500 mb-3" data-id="element-1169">
                    {group.month}
                  </h3>

                  {/* Table Card */}
                  <Card className="overflow-hidden" data-id="element-1170">
                    <div className="overflow-x-auto" data-id="element-1171">
                      <table className="w-full" data-id="element-1172">
                        <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1173">
                          <tr data-id="element-1174">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1175">
                              Data
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1176">
                              Problema
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1177">
                              Gravità
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1178">
                              Risolto da
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1179">
                              Tempo
                            </th>
                            <th className="px-4 py-3" data-id="element-1180"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200" data-id="element-1181">
                          {group.interventions.map((intervention, index) => <motion.tr key={intervention.id} initial={{
                      opacity: 0,
                      y: 10
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} transition={{
                      delay: groupIndex * 0.1 + index * 0.05
                    }} onClick={() => handleInterventionClick(intervention.id, 'maintenance')} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-1182">
                              <td className="px-4 py-4 text-sm font-medium text-navy" data-id="element-1183">
                                {intervention.dateFormatted}
                              </td>
                              <td className="px-4 py-4" data-id="element-1184">
                                <div className="flex items-center gap-2" data-id="element-1185">
                                  <span className="text-lg" data-id="element-1186">
                                    {intervention.problemEmoji}
                                  </span>
                                  <div data-id="element-1187">
                                    <p className="text-sm font-medium text-gray-900" data-id="element-1188">
                                      {intervention.problemType}
                                    </p>
                                    <p className="text-xs text-gray-500" data-id="element-1189">
                                      {intervention.description}
                                    </p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4" data-id="element-1190">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${intervention.severity === 'severe' ? 'bg-red-100 text-red-700' : intervention.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-1191">
                                  {t.host.interventionsHistory.severity[intervention.severity]}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1192">
                                {intervention.resolvedBy}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600" data-id="element-1193">
                                {intervention.interventionTime}
                              </td>
                              <td className="px-4 py-4 text-right" data-id="element-1194">
                                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-deep transition-colors inline-block" data-id="element-1195" />
                              </td>
                            </motion.tr>)}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>)}
            </motion.div>}
        </AnimatePresence>
      </main>
    </div>;
}